import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { ReactElement } from "react";
import * as Yup from "yup";

import { palette } from "~frontend/theme";
import { useErrorMessageFormatter } from "~frontend/util/errors";

import { FlexBox } from "../FlexBox";
import { ExternalLink } from "../Link";
import {
  FormikDialog,
  FormikSubmitButton,
  FormikTextField,
  submitErrorField,
} from "../forms";

const RESPONSE_SCRIPT_GSHEET_IMPORT_TEMPLATE_URL =
  "https://docs.google.com/spreadsheets/d/1pEF1jtyci2CAapuzUCb3gGAXf2YIAsFgbpn_uRbBmC0/copy";

const RESPONSE_SCRIPT_GSHEET_IMPORT_HELPDESK_ARTICLE_URL =
  "https://support.scaletowin.com/response-scripts-imports";

export default function ImportScriptsDialog({
  open,
  onClose,
  onSubmit,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: any) => Promise<void>;
}): ReactElement {
  const getErrorMessage = useErrorMessageFormatter();
  return (
    <FormikDialog
      open={open}
      initialValues={{
        [submitErrorField]: null,
        gsheetUrl: "",
      }}
      validationSchema={Yup.object().shape({
        gsheetUrl: Yup.string()
          .label("Google Sheets url")
          .required("${label} is required")
          .url()
          .test("is-google-url", "Invalid ${label}", value => {
            return value
              ? URL.parse(value)?.hostname === "docs.google.com"
              : false;
          }),
      })}
      onSubmit={async (values, formik) => {
        try {
          await onSubmit(values);
          formik.resetForm();
        } catch (e) {
          formik.setErrors({
            [submitErrorField]: getErrorMessage(e),
          });
        }
      }}
      onClose={onClose}
    >
      {({ isSubmitting, isValid, errors }) => {
        return (
          <>
            <DialogTitle>Import Scripted Responses</DialogTitle>
            <DialogContent>
              <Typography variant="body1">
                You can import Response Scripts and with associated Tags and
                Response Groups by pasting a Google Sheet URL below.{" "}
                <ExternalLink href={RESPONSE_SCRIPT_GSHEET_IMPORT_TEMPLATE_URL}>
                  Use this template
                </ExternalLink>{" "}
                to format your Google Sheet.
              </Typography>
              <Typography variant="body1" sx={{ mb: "16px" }}>
                <br /> Read more about{" "}
                <ExternalLink
                  href={RESPONSE_SCRIPT_GSHEET_IMPORT_HELPDESK_ARTICLE_URL}
                >
                  how to import scripts here
                </ExternalLink>
                .
              </Typography>
              <FormikTextField
                name="gsheetUrl"
                label="URL for the Google Sheet you want to import"
                fullWidth
              />
              <Typography variant="body2" sx={{ mb: "16px" }}>
                <strong>Note:</strong> Any response scripts you've previously
                imported from this sheet will be replaced (scripts that you
                created manually or imported from a different sheet will not be
                affected).
              </Typography>
            </DialogContent>
            <DialogActions
              sx={{
                justifyContent: "space-between",
                pt: 0,
                px: "24px",
                pb: "20px",
              }}
            >
              <Box sx={{ width: "250px" }}>
                {errors[submitErrorField] && (
                  <Typography variant="body2" sx={{ color: palette.red }}>
                    {errors[submitErrorField]}
                  </Typography>
                )}
              </Box>
              <FlexBox gap="10px">
                <Button
                  disabled={isSubmitting}
                  variant="contained"
                  color="tertiary"
                  onClick={onClose}
                >
                  Cancel
                </Button>
                <FormikSubmitButton
                  disabled={!isValid}
                  submitText="Start import"
                  loadingText="Importing..."
                />
              </FlexBox>
            </DialogActions>
          </>
        );
      }}
    </FormikDialog>
  );
}
